<template>
    <div class="text-editor-container d-flex flex-column" style="height: 100%; width: 100%; max-height: 100vh; overflow-y: auto;">
        <v-btn v-if="true" icon class="back-button ma-3" style="position: absolute; top: 0; left: 0; z-index: 1000;" @click="$emit('back')">
            <lucide-arrow-left :size="24" style="width: 24px; height: 24px;"></lucide-arrow-left>
        </v-btn>

        <div style="max-width: 650px;" class="d-flex flex-column text-center align-center mx-auto pt-12">
            <h2 style="font-size: 24px;">Pega tu texto</h2>
            <span style="font-size: 16px;" class="my-2">Pega o escribe el texto que quieres convertir en materiales de estudio</span>
        </div>

        <div class="flex-grow-1 d-flex flex-column pa-4" style="max-width: 650px; margin: 0 auto; width: 100%; padding-bottom: 100px !important;">
            <v-text-field
                v-model="documentName"
                label="Nombre del documento"
                placeholder="Introduce un nombre para tu documento"
                outlined
                dense
                class="mb-4"
                style="max-height: 48px;"
                hide-details
            ></v-text-field>
            
            <v-textarea v-model="textContent" class="flex-grow-1 text-editor" outlined auto-grow hide-details rows="20"
                placeholder="Pega o escribe aquí tu texto..."></v-textarea>

            <!-- Study Configurator Component -->
            <div class="mt-8" style="width: 100%;">
              <h2 style="font-size: 24px; text-align: center; margin-bottom: 16px;">Configuración del estudio</h2>
              <span style="font-size: 16px; text-align: center; display: block;" class="my-2">Escoje el lenguage y los tipos de preguntas.</span>
              <study-configurator ref="studyConfig" :is-premium="isPremium" @show-premium-dialog="showFeaturesDialog = true" />
            </div>
        </div>

        <div class="d-flex justify-end pa-4 white"
         style="border-top: 1px solid #dedede !important; margin: 0 auto; width: 100%; position: fixed; bottom: 0; left: 0; right: 0;">
            <v-btn color="primary" class="primary-btn" :disabled="!textContent.trim()" @click="saveDocument"
                :loading="isSaving">
                Generar materiales
            </v-btn>
        </div>

        <v-dialog v-model="showProcessingDialog" persistent max-width="400">
            <v-card>
                <v-card-title class="text-h5 text-center pa-4">
                    Generando materiales
                </v-card-title>
                <v-card-text class="text-center">
                    <v-progress-circular indeterminate color="primary" size="64" width="5"
                        class="mb-4"></v-progress-circular>
                    <p class="mt-4">Estamos procesando tu texto y generando materiales de estudio. Esto puede tardar
                        unos segundos...</p>
                </v-card-text>
            </v-card>
        </v-dialog>

        <features-dialog :showDialog.sync="showFeaturesDialog" />
    </div>
</template>

<script>
import { ArrowLeft } from 'lucide-vue'
import axios from 'axios'
import StudyConfigurator from './StudyConfigurator.vue';
import FeaturesDialog from '@/components/premium/FeaturesDialog.vue';

export default {
    name: 'TextEditor',
    components: {
        LucideArrowLeft: ArrowLeft,
        StudyConfigurator,
        FeaturesDialog
    },
    mounted() {
        this.$emit('disable-auto-selecting-document');
    },
    data() {
        return {
            textContent: '',
            documentName: '',
            isSaving: false,
            showProcessingDialog: false,
            showFeaturesDialog: false,
        }
    },
    computed: {
        isPremium() {
            return this.$root.currentUser && this.$root.currentUser.premium;
        },
    },
    methods: {
        async saveDocument() {
            this.isSaving = true;
            this.saveError = null;

            try {
                const content = this.textContent;
                if (!content || content.trim().length === 0) {
                    this.$toast.error('El texto no puede estar vacío');
                    return;
                }

                const response = await axios.post('/documents/text', {
                    content: content,
                    title: this.documentName || 'Documento sin título',
                    user_id: this.$root.currentUser.userId
                }, {
                    headers: { "Authorization": this.$root.token.value }
                });

                if (response.data.success) {
                    const documentId = response.data.documentId;
                    this.processingStepText = "Generando materiales de estudio...";
                    this.isGeneratingContent = true;

                    try {
                        const config = this.$refs.studyConfig.getConfig();
                        const contentResponse = await axios.post('/generate-all-content', {
                            documentId,
                            user_id: this.$root.currentUser.userId,
                            contentTypes: ['flashcards', 'quiz', 'summary'],
                            language: config.selectedLanguage,
                            questionTypes: config.selectedQuestionTypes
                        }, {
                            headers: { "Authorization": this.$root.token.value }
                        });

                        if (contentResponse.data.success) {
                            // Asignar el documento a la asignatura si se seleccionó una
                            if (config.selectedSubject) {
                                await this.$refs.studyConfig.assignDocumentToSubject(documentId);
                            }

                            this.$emit('document-saved', {
                                documentId,
                                fileName: this.documentName,
                                ...contentResponse.data
                            });

                            this.isGeneratingContent = false;
                            this.$router.push(`/app/document/${documentId}`);
                        } else {
                            this.$toast.warning('El documento se guardó, pero hubo un problema al generar el contenido');
                            this.$emit('document-saved', {
                                documentId,
                                fileName: this.documentName,
                                generationError: true
                            });
                        }
                    } catch (generationError) {
                        console.error('Error generando contenido:', generationError);
                        this.$toast.error('Error al generar el contenido. Puedes intentar generarlo más tarde.');

                        this.$emit('document-saved', {
                            documentId,
                            fileName: this.documentName,
                            generationError: true
                        });
                    }

                    this.processingComplete = true;
                    
                    // Reiniciar después de mostrar el mensaje de éxito
                    setTimeout(() => {
                        this.processingComplete = false;
                        this.textContent = '';
                        this.documentName = '';
                    }, 3000);

                } else {
                    this.saveError = 'Error al guardar el documento';
                    this.$toast.error(this.saveError);
                }
            } catch (error) {
                console.error('Error saving document:', error);
                this.saveError = 'Error al guardar el documento. Por favor, inténtalo de nuevo.';
                this.$toast.error(this.saveError);
            } finally {
                this.isSaving = false;
            }
        }
    }
}
</script>

<style scoped>
.text-editor-container {
    background-color: white;
}

.text-editor {
    font-family: 'Rubik', sans-serif;
    font-size: 16px;
}

.v-textarea.text-editor>>>textarea {
    min-height: 400px;
}

.primary-btn {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 800 !important;
    font-size: 0.9rem !important;
    border-radius: 13px !important;
    transition: filter 0.2s ease, transform 0.1s ease !important;
    height: 38px !important;
    max-height: 38px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
    border: 2px solid #4dd453 !important;
    background-color: #4dd453 !important;
    box-shadow: 0 4px 0 #42b848 !important;
    color: white;
}

.primary-btn:hover {
    filter: brightness(1.02);
    transform: translateY(0) !important;
}

.primary-btn:active {
    box-shadow: 0 0 0 #42b848 !important;
    transform: translateY(4px) !important;
}

.primary-btn:disabled {
    border: 2px solid #E0E0E0 !important;
    box-shadow: 0 4px 0 #9a9a9a !important;
}

.v-text-field>>>.v-input__slot {
    min-height: 42px !important;
}

.v-text-field>>>.v-text-field__slot input {
    font-size: 14px;
    font-family: "Nunito", "DINRounded", "Inter", sans-serif !important;
}
</style>